import { FC, HTMLAttributes, ReactNode } from 'react';

import { GridProps } from '@purpur/library';
import { Icon } from '@purpur/library/icon';
import { chevronLeft } from '@purpur/library/icon/assets/chevron-left';
import { chevronRight } from '@purpur/library/icon/assets/chevron-right';
import { EmblaCarouselType } from 'embla-carousel';
import useEmblaCarousel from 'embla-carousel-react';

import {
    CarouselButtons,
    CarouselContainer,
    CarouselControls,
    CarouselSlide,
    CarouselViewport,
    CarouselWrapper,
    NextButton,
    PrevButton,
    Progress,
    ProgressBar,
} from './Carousel.styles';
import { usePrevNextButtons } from './hooks/usePrevNextButtons';
import { useProgressBar } from './hooks/useProgressBar';

type DivProps = HTMLAttributes<HTMLDivElement>;
type Inheritables =
    | ({ variant?: 'full-width' } & DivProps)
    | ({ variant?: 'dynamic-width' } & DivProps & Pick<GridProps, 'containerClassName'>);

export type CarouselProps = {
    slides: ReactNode[];
    variant?: 'dynamic-width' | 'full-width';
    noControlsMargin?: boolean;
} & Inheritables;

function isScrollable(emblaApi: EmblaCarouselType) {
    return emblaApi.canScrollNext() || emblaApi.canScrollPrev();
}

export const Carousel: FC<CarouselProps> = ({
    slides,
    variant = 'full-width',
    noControlsMargin,
    ...inheritedProps
}) => {
    const [slider, emblaApi] = useEmblaCarousel({
        watchDrag: (embla) => isScrollable(embla),
        loop: false,
    });
    const { scrollProgress } = useProgressBar(emblaApi, slides.length);
    const { prevBtnDisabled, nextBtnDisabled, onPrevButtonClick, onNextButtonClick } = usePrevNextButtons(emblaApi);

    const isVariableWidth = variant === 'dynamic-width';
    const controlsHiddenXlg = isVariableWidth && slides.length <= 4;
    const controlsHiddenLg = isVariableWidth && slides.length <= 3;
    const controlsHiddenMd = isVariableWidth && slides.length <= 2;
    const controlsHiddenSm = slides.length <= 1;

    return (
        <CarouselWrapper $isVariableWidth={isVariableWidth} {...inheritedProps}>
            <CarouselViewport ref={slider}>
                <CarouselContainer $isVariableWidth={isVariableWidth}>
                    {slides.map((slide, index) => (
                        <CarouselSlide
                            $isVariableWidth={isVariableWidth}
                            $controlsHiddenXlg={controlsHiddenXlg}
                            $controlsHiddenLg={controlsHiddenLg}
                            $controlsHiddenMd={controlsHiddenMd}
                            $controlsHiddenSm={controlsHiddenSm}
                            key={index}
                        >
                            {slide}
                        </CarouselSlide>
                    ))}
                </CarouselContainer>
            </CarouselViewport>
            <CarouselControls
                $controlsHiddenXlg={controlsHiddenXlg}
                $controlsHiddenLg={controlsHiddenLg}
                $controlsHiddenMd={controlsHiddenMd}
                $controlsHiddenSm={controlsHiddenSm}
                $noControlsMargin={noControlsMargin}
            >
                <Progress>
                    <ProgressBar style={{ transform: `translate3d(${scrollProgress}%, 0px, 0px)` }} />
                </Progress>

                <CarouselButtons>
                    <PrevButton
                        onClick={onPrevButtonClick}
                        disabled={prevBtnDisabled}
                        aria-hidden={true}
                        data-element="Slider Button"
                        data-element-type="Back"
                    >
                        <Icon svg={chevronLeft} size="xs" />
                    </PrevButton>
                    <NextButton
                        onClick={onNextButtonClick}
                        disabled={nextBtnDisabled}
                        aria-hidden={true}
                        data-element="Slider Button"
                        data-element-type="Forward"
                    >
                        <Icon svg={chevronRight} size="xs" />
                    </NextButton>
                </CarouselButtons>
            </CarouselControls>
        </CarouselWrapper>
    );
};
